import { useEffect, useRef, useState } from "react";
import { Check, Share2 } from "lucide-react";

import { Hint } from "@/design-system/Hint.tsx";
import { shareUrlFor } from "@/share/share.url.ts";
import { money } from "@/listings/listing.format.ts";
import type { Listing } from "@housing/shared";
import { cn } from "@/design-system/cn.ts";

/**
 * Hand this listing's link to whatever the platform does best.
 *
 * `navigator.share` is the one worth reaching for on a phone: it raises the
 * system sheet, so the link goes straight into Messages or a group chat with no
 * detour through the clipboard. Desktop browsers largely don't implement it, so
 * copying is a first-class path rather than an apology — and it says so, since
 * a button that silently succeeds is indistinguishable from one that's broken.
 */
export function ShareButton({ listing, className }: { listing: Listing; className?: string }) {
  const [copied, setCopied] = useState(false);
  const timer = useRef(0);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  // A fresh listing means the previous "Copied" tick is about a link the user is
  // no longer looking at.
  useEffect(() => setCopied(false), [listing.id]);

  const flash = () => {
    setCopied(true);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setCopied(false), 1800);
  };

  const share = async () => {
    const url = shareUrlFor(listing.id);
    const payload = {
      title: `${money(listing.price)} — ${listing.address ?? "SF rental"}`,
      text: listing.address ?? listing.title ?? "A listing on RADAR",
      url,
    };

    if (typeof navigator.share === "function" && (navigator.canShare?.(payload) ?? true)) {
      try {
        await navigator.share(payload);
        return;
      } catch (err) {
        // Dismissing the system sheet is a decision, not a failure. Copying the
        // link anyway would be overruling it.
        if (err instanceof Error && err.name === "AbortError") return;
      }
    }

    try {
      await navigator.clipboard.writeText(url);
      flash();
    } catch (err) {
      // Unreachable in practice — the clipboard API needs a secure context, and
      // so does everything else here. Worth a line in the console rather than a
      // toast: nothing about the page is broken, one button just did nothing.
      console.warn("[share] clipboard unavailable", err);
    }
  };

  return (
    <Hint
      label={copied ? "Link copied" : "Share this listing"}
      detail={copied ? undefined : "Copies a link that opens the map on this unit."}
      side="top"
    >
      <button
        type="button"
        onClick={() => void share()}
        aria-label="Share this listing"
        className={cn(
          "grid shrink-0 place-items-center rounded-lg border border-rule bg-ink-900/85 px-3 py-2.5 text-chalk-dim transition",
          "hover:border-rule-bright hover:text-chalk active:scale-95",
          "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-signal-ink",
          copied && "border-signal text-signal",
          className,
        )}
      >
        {copied ? <Check className="size-4" /> : <Share2 className="size-4" />}
      </button>
    </Hint>
  );
}
